'use client';
import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

declare global {
  interface Window { gtag?: (...args: any[]) => void; dataLayer?: any[]; }
}

const SID_KEY = 'ex_sid';
const SID_TS_KEY = 'ex_sid_ts';
const SESSION_MAX_MS = 30 * 60 * 1000; // 30-min inactivity window
const CLICKID_KEY = 'ex_clickid';

export { getSessionId } from '@/lib/trackerSession';
import { getSessionId } from '@/lib/trackerSession';
import { abCtx } from '@/lib/abClient';
import { makeScrollDepth, waHandoff, isWaHref } from '@/lib/funnelTrack'; // 14/10/26 funnel events

// Google Ads click ids — the ONE implementation is lib/trackClient.mjs (shared with the 3D journey's window.ExTrack, 04/10/26).
// @ts-ignore — plain browser JS module
import { getClickIds as clickIdsImpl, interacted } from '@/lib/trackClient.mjs';
export function getClickIds(): { gclid: string | null; wbraid: string | null; gbraid: string | null } { return clickIdsImpl(); }

function clientCtx(startedAt: number, maxScrollPct: number) {
  const p = new URLSearchParams(typeof location !== 'undefined' ? location.search : '');
  return {
    screenW: window.screen?.width, screenH: window.screen?.height,
    viewportW: window.innerWidth, viewportH: window.innerHeight,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    referrer: document.referrer || null,
    durationSec: Math.round((Date.now() - startedAt) / 1000),
    maxScrollPct,
    utmSource: p.get('utm_source'), utmMedium: p.get('utm_medium'), utmCampaign: p.get('utm_campaign'),
    utmContent: p.get('utm_content'), utmTerm: p.get('utm_term'),
    ...getClickIds(),
    interacted: interacted(), // bot score (lib/botScore.ts, 06/10/26)
  };
}

export default function Tracker() {
  const pathname = usePathname();
  const startedAt = useRef(Date.now());
  const sid = useRef<string>('');
  const maxScrollPct = useRef(0);
  const exitSent = useRef(false);   // one exit per page view (see the leave effect below)
  const scrollDepth = useRef(makeScrollDepth());
  const internalNavAt = useRef(0);  // when the visitor last started moving to another page of ours

  function send(event: string, extra: Record<string, any> = {}, beacon = false) {
    try {
      if (!sid.current) sid.current = getSessionId();
      const payload = JSON.stringify({
        event, sessionId: sid.current,
        page: location.pathname + location.search,
        client: clientCtx(startedAt.current, maxScrollPct.current),
        ...abCtx(), // A/B: the design this page rendered (lib/abClient.ts)
        ...extra,
      });
      if (beacon && navigator.sendBeacon) {
        navigator.sendBeacon('/api/track', new Blob([payload], { type: 'text/plain' }));
      } else {
        fetch('/api/track', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true }).catch(() => {});
      }
    } catch {}
  }

  // pageview on every route change — also resets scroll depth for the new page
  useEffect(() => {
    maxScrollPct.current = 0; scrollDepth.current.reset();
    exitSent.current = false; internalNavAt.current = 0; // a new page view: its own leave, if any, is a new fact
    send('pageview', { categorySlug: (() => { try { return decodeURIComponent(location.pathname.split('/')[1] || '') || null; } catch { return null; } })() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // scroll depth (throttled via rAF), heartbeat (updates time-on-site) + flush on leave
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const doc = document.documentElement;
        const scrollable = doc.scrollHeight - doc.clientHeight;
        const pct = scrollable > 0 ? Math.round((window.scrollY / scrollable) * 100) : 100;
        if (pct > maxScrollPct.current) maxScrollPct.current = Math.min(100, pct);
        if (!/^\/(admin|pro|x)(\/|$)/.test(location.pathname)) scrollDepth.current.onScroll();
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    const hb = setInterval(() => { if (document.visibilityState === 'visible') send('heartbeat'); }, 20000);
    // EXIT = one per real leave (bughunter 30/09, s_muny5symqtgpq2wv): `visibilitychange→hidden` AND `pagehide` both fired
    // on every unload, so each leave logged TWICE, and a full-page hop to another experts.co.il page logged an "exit" too.
    // Now: (a) at most one exit per page view — re-armed only when the visitor comes BACK to this page (tab switch and
    // return, bfcache restore), so the next leave is a new fact; (b) an in-site navigation is not an exit — a same-origin
    // link click / form submit / Navigation-API navigate marks the next few seconds as "moving inside the site".
    const markInternal = () => { internalNavAt.current = Date.now(); };
    // same-origin AND a real page of ours: /wa/ /tel/ /wa-amp/ short links redirect OUT (WhatsApp / dialer) — that is a leave
    const sameOrigin = (href: string) => { try { const u = new URL(href, location.href); return u.origin === location.origin && !/^\/(wa|tel|wa-amp)\//.test(u.pathname); } catch { return false; } };
    const hashOnly = (href: string) => { try { const u = new URL(href, location.href); return u.pathname === location.pathname && u.search === location.search && !!u.hash; } catch { return false; } };
    const onClickCapture = (e: MouseEvent) => {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; // opens a new tab/window — this page stays
      const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (a && isWaHref(a.href)) waHandoff('link', { href: a.pathname.slice(0, 40) }); // WA hand-off outcome (anchors)
      if (!a || a.hasAttribute('download') || (a.target && a.target !== '_self')) return;
      if (hashOnly(a.href)) return; // in-page anchor — no navigation at all
      if (sameOrigin(a.href)) markInternal(); else internalNavAt.current = 0;
    };
    const onSubmitCapture = (e: SubmitEvent) => { const f = e.target as HTMLFormElement | null; if (f && sameOrigin(f.action || location.href)) markInternal(); };
    const nav: any = (window as any).navigation;
    const onNavigate = (e: any) => { const url = e?.destination?.url; if (url && !e.hashChange && sameOrigin(url)) markInternal(); };
    const leave = () => {
      if (exitSent.current) return;
      if (internalNavAt.current && Date.now() - internalNavAt.current < 5000) return; // moving to another page of ours — not a leave
      exitSent.current = true;
      send('exit', {}, true);
    };
    const onVisibility = () => { if (document.visibilityState === 'hidden') leave(); else { exitSent.current = false; internalNavAt.current = 0; } };
    const onPageShow = (e: PageTransitionEvent) => { if (e.persisted) { exitSent.current = false; internalNavAt.current = 0; } };
    document.addEventListener('click', onClickCapture, true);
    document.addEventListener('submit', onSubmitCapture, true);
    nav?.addEventListener?.('navigate', onNavigate);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', leave);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('scroll', onScroll);
      clearInterval(hb);
      document.removeEventListener('click', onClickCapture, true);
      document.removeEventListener('submit', onSubmitCapture, true);
      nav?.removeEventListener?.('navigate', onNavigate);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', leave);
      window.removeEventListener('pageshow', onPageShow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

// Imperative helper for custom events elsewhere (contact clicks, etc.)
export function trackEvent(event: string, extra: Record<string, any> = {}) {
  try {
    const sid = getSessionId();
    const payload = JSON.stringify({
      event, sessionId: sid,
      page: location.pathname + location.search,
      ...abCtx(),
      client: {
        screenW: window.screen?.width, screenH: window.screen?.height,
        viewportW: window.innerWidth, viewportH: window.innerHeight,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        referrer: document.referrer || null,
        ...getClickIds(),
        interacted: interacted(),
      },
      ...extra,
    });
    fetch('/api/track', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true }).catch(() => {});
  } catch {}
}
